import { PrinterOutlined } from '@ant-design/icons';
import { App, Button, Flex, Form, Modal } from 'antd';
import { useMemo, useState } from 'react';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';
import { getLocale } from 'translations/locale';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import config from 'config';
import { useRingValidationV1ControllerExportPdfs } from 'generated/apiComponents';
import { Module_Enum } from 'generated/graphql';
import { downloadFile } from 'technical/print';
import { isUndefinedOrNull } from 'technical/utils/is-null-or-undefined';
import { NumberRangeValue } from 'ui/form/number-range-input';
import { NumberRangeInputWithMessage } from 'ui/form/number-range-input-with-message';

type ExportFormValues = {
  ringRange: NumberRangeValue;
};

const ExportModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { currentConstructionSiteId } = useAppContext();
  invariant(currentConstructionSiteId, 'No constructionSite id');

  const { message } = App.useApp();
  const [form] = Form.useForm<ExportFormValues>();
  const ringRange = Form.useWatch('ringRange', form);
  const { mutateAsync: exportPdfs, isPending } =
    useRingValidationV1ControllerExportPdfs();

  const headers = useRestApiHeaders(Module_Enum.QualityControl);

  const ringCount = useMemo(() => {
    const [from, to] = ringRange ?? [];
    if (isUndefinedOrNull(from) || isUndefinedOrNull(to)) {
      return 0;
    }
    return to - from + 1;
  }, [ringRange]);

  const handleSubmit = async (values: ExportFormValues) => {
    const [from, to] = values.ringRange ?? [];
    if (isUndefinedOrNull(from) || isUndefinedOrNull(to)) {
      return;
    }

    try {
      const result = await exportPdfs({
        body: { fromRingNumber: from, toRingNumber: to, locale: getLocale() },
        pathParams: { constructionSiteId: currentConstructionSiteId },
        headers,
      });

      if (result.url) {
        const extension = result.url.endsWith('.zip') ? 'zip' : 'pdf';
        downloadFile(
          `${config.gcp.publicUri}/${result.url}`,
          `${t('qualityControl.ringComplianceReport.printReportsModal.folderName')}-${from}-${to}.${extension}`,
        );
      }

      message.open({
        type: 'success',
        content: t(
          'qualityControl.ringComplianceReport.printReportsModal.success',
        ),
      });
      onClose();
    } catch {
      message.open({
        type: 'error',
        content: t(
          'qualityControl.ringComplianceReport.printReportsModal.error',
        ),
      });
    }
  };

  return (
    <Modal
      title={t('qualityControl.ringComplianceReport.printReportsModal.title')}
      open={isOpen}
      onCancel={onClose}
      destroyOnHidden
      footer={
        <Flex gap={8} justify="flex-end">
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            type="primary"
            loading={isPending}
            disabled={ringCount === 0}
            onClick={form.submit}
          >
            {t('common.confirm')}
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap={16}>
        {t('qualityControl.ringComplianceReport.printReportsModal.description')}
        <Form form={form} layout="inline" onFinish={handleSubmit}>
          <NumberRangeInputWithMessage
            name="ringRange"
            leftPlaceholder={t('input.ringRange.startPlaceholder')}
            rightPlaceholder={t('input.ringRange.endPlaceholder')}
          />
        </Form>
      </Flex>
    </Modal>
  );
};

export const RingCompliancePrintReportsButton = () => {
  const { t } = useTranslation();
  const [isModalOpen, setModalOpen] = useState(false);

  return (
    <>
      <Button
        icon={<PrinterOutlined />}
        iconPosition="end"
        onClick={() => setModalOpen(true)}
      >
        {t('common.printBulk')}
      </Button>
      <ExportModal isOpen={isModalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};
